import {BounceRole} from "@bounce/shared/consumer/declarations";
import NiceModal from "@ebay/nice-modal-react";
import {useRewardsProgramState} from "~/components/rewards/hooks/useRewardsProgramState";
import {useCallback, useMemo} from "react";
import {generatePath, useNavigate} from "react-router-dom";
import {useGetPaymentMethods, useGetDashboardQuery, useGetAccount} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {KEY_OPEN_CANCEL_PLAN_DIALOG} from "~/constants/no-login-keys";
import {useAuth} from "@bounce/shared/contexts";
import {useAmplitude} from "~/contexts";
import {useReschedulePlan} from "~/pages/account/account-dashboard/hooks/useReschedulePlan";
import {urls} from "~/routes/consumerUrls";
import {AddPaymentMethodDialog, EnableAutoPayDialog, OneTimePaymentDialog, PreCancelPlanDialog} from "../dialogs";
import {useAccountId} from "~/hooks";

interface Props {
  onClose: () => void;
}

export const useDashboardMenuContent = ({onClose}: Props) => {
  const accountId = useAccountId();
  const {data: account} = useGetAccount({accountId}, {enabled: !!accountId});
  const activePaymentPlan = useMemo(() => account?.active_payment_plan, [account]);
  const {userRole} = useAuth();
  const navigate = useNavigate();
  const {pageClickEvent} = useAmplitude();
  const {rewardsProgramState, shouldDisplayRewardsWarning} = useRewardsProgramState();

  const activePaymentPlanId = activePaymentPlan?.payment_plan_id;
  const userDashboard = useGetDashboardQuery(
    {
      account_id: accountId,
      payment_plan_id: activePaymentPlanId,
    },
    {enabled: !!activePaymentPlanId}
  );

  const frequency = useMemo(() => {
    if (userDashboard.data) {
      return userDashboard.data.installment_frequency;
    }
  }, [userDashboard.data]);

  const {data: allPaymentMethods} = useGetPaymentMethods(
    {
      accountId,
      payment_plan_id: activePaymentPlanId,
    },
    {
      enabled: !!accountId && !!activePaymentPlanId,
    }
  );
  const pendingAmount = userDashboard?.data?.pending_amount || 0;
  const planBalance = userDashboard.data?.amount_unpaid || 0;
  const autoPayEnrolled = useMemo(() => {
    if (userDashboard.data) {
      return userDashboard.data.autopay_enrolled;
    }
  }, [userDashboard.data]);
  const isAccountResolved = activePaymentPlan?.state === "resolved";
  const isAccountLate = activePaymentPlan?.state === "late";
  const isEligibleForRewards = ["active", "unavailable"].includes(rewardsProgramState?.program_state ?? "");

  const shouldDisplayDashboardMenu = Boolean(activePaymentPlan) && !isAccountResolved;
  const shouldDisplayCancelMyPlan = Boolean(frequency);
  const shouldDisplaySwitchToAutoPay = !autoPayEnrolled;
  const shouldHideOneTimePayment = activePaymentPlan?.payment_plan_type === "BUNDLE";
  const shouldDisableOneTimePayment = isAccountLate && isEligibleForRewards;

  const onOneTimePaymentClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "one_time_payment"});
    onClose();
    await NiceModal.show(OneTimePaymentDialog, {
      planBalance,
      accountId,
      activePaymentPlanId,
      pendingAmount,
    });
  }, [onClose, planBalance, accountId, activePaymentPlanId, pendingAmount]);

  const onCancelMyPlanClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "cancel_plan"});
    onClose();
    if (userRole === BounceRole.NO_LOGIN_USER) {
      sessionStorage.setItem(KEY_OPEN_CANCEL_PLAN_DIALOG, "true");
      navigate(generatePath(urls.accountDashboard.url, {accountId}));
    } else {
      await NiceModal.show(PreCancelPlanDialog, {
        accountId,
        shouldDisplayRewardsWarning,
      });
    }
  }, [onClose, accountId, userRole, navigate, shouldDisplayRewardsWarning]);

  const onSwitchToAutoPayClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "switch_autopay"});
    try {
      onClose();
      await NiceModal.show(EnableAutoPayDialog, {
        onEnableAutoPay: async () => {
          await NiceModal.show(AddPaymentMethodDialog, {
            accountId,
            title: "Add Payment Method for AutoPay",
            activePaymentPlanId,
          });
        },
      });
    } catch {}
  }, [onClose, accountId, activePaymentPlanId]);

  const onManagePaymentMethod = useCallback(async () => {
    onClose();
    if (allPaymentMethods?.length === 0) {
      await NiceModal.show(AddPaymentMethodDialog, {
        accountId,
        activePaymentPlanId,
        inManagePaymentMethod: true,
      });
    } else {
      navigate(generatePath(urls.managePaymentMethod.url, {accountId}));
    }
  }, [onClose, allPaymentMethods, navigate, accountId]);

  const {handleReschedulePlan, shouldDisplayReschedulePlan} = useReschedulePlan({
    userDashboard: userDashboard.data,
    paymentPlanId: activePaymentPlanId,
    accountId,
    page: Pages.MENU,
    onClose,
  });

  return {
    shouldDisplayDashboardMenu,
    shouldDisplayCancelMyPlan,
    shouldHideOneTimePayment,
    shouldDisableOneTimePayment,
    shouldDisplaySwitchToAutoPay,
    shouldDisplayReschedulePlan,
    onOneTimePaymentClick,
    onReschedulePlanClick: handleReschedulePlan,
    onCancelMyPlanClick,
    onSwitchToAutoPayClick,
    onManagePaymentMethod,
  };
};
